/**
 * Authentication Service
 * Handles login, logout, token management
 */

import { MAINAPIURL } from "../pages/appsettings";

const TOKEN_KEYS = {
  ACCESS_TOKEN: "ACCESS_TOKEN",
  REFRESH_TOKEN: "REFRESH_TOKEN",
  USER: "USER",
  PERMISSIONS: "PERMISSIONS",
};

/**
 * Store tokens and user data
 */
export const setAuthData = (data) => {
  // Clear any existing permission keys to prevent cross-account pollution
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith("USERPERMISSION_")) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key));

  localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, data.accessToken);
  localStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, data.refreshToken);
  localStorage.setItem(TOKEN_KEYS.USER, JSON.stringify(data.user));

  // Store permissions in the old format for backward compatibility
  if (data.permissions) {
    data.permissions.forEach((perm) => {
      const permissionName = String(perm.per_name || "").trim();
      if (!permissionName) {
        return;
      }

      // per_status = 1 means enabled, store as 'false' (not disabled)
      // per_status = 0 means disabled, store as 'true' (disabled)
      const isDisabled = Number(perm.per_status) !== 1;
      localStorage.setItem(
        `USERPERMISSION_${permissionName}`,
        isDisabled.toString(),
      );
    });
  }

  // Legacy compatibility
  localStorage.setItem("APIURL", MAINAPIURL);
  localStorage.setItem("USERID", data.user.id);
  localStorage.setItem("USERDPNAME", data.user.displayName);
  localStorage.setItem("USERIMAGE", data.user.photo);
  localStorage.setItem("USERDESIGNATION", data.user.designation);
};

/**
 * Get access token
 */
export const getAccessToken = () => {
  return localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN);
};

/**
 * Get an arbitrary auth-related value from localStorage
 * (used for legacy USERPERMISSION_* / USERID keys)
 */
export const getAuthItem = (key) => {
  return localStorage.getItem(key);
};

/**
 * Get refresh token
 */
export const getRefreshToken = () => {
  return localStorage.getItem(TOKEN_KEYS.REFRESH_TOKEN);
};

/**
 * Get user data
 */
export const getUser = () => {
  const user = localStorage.getItem(TOKEN_KEYS.USER);
  return user ? JSON.parse(user) : null;
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = () => {
  return !!getAccessToken();
};

/**
 * Clear all auth data
 */
export const clearAuthData = () => {
  // Clear new token keys
  Object.values(TOKEN_KEYS).forEach((key) => {
    localStorage.removeItem(key);
  });

  // Clear legacy keys
  localStorage.removeItem("APIURL");
  localStorage.removeItem("AUTHCODE");
  localStorage.removeItem("USERID");
  localStorage.removeItem("USERDPNAME");
  localStorage.removeItem("USERIMAGE");
  localStorage.removeItem("USERDESIGNATION");
  localStorage.removeItem("APPVERSION");

  // Clear all permission keys
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith("USERPERMISSION_")) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key));
};

/**
 * Login
 */
export const login = async (username, password) => {
  const response = await fetch(`${MAINAPIURL}/auth/login.php`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ username, password }),
  });

  const data = await response.json();

  if (!response.ok || data.status === "error") {
    throw new Error(data.message || "Login failed");
  }

  setAuthData(data);
  return data;
};

/**
 * Refresh access token
 */
export const refreshAccessToken = async () => {
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    throw new Error("No refresh token available");
  }

  const response = await fetch(`${MAINAPIURL}/auth/refresh.php`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ refreshToken }),
  });

  const data = await response.json();

  if (!response.ok || data.status === "error") {
    // Refresh token is invalid, clear auth data
    clearAuthData();
    throw new Error(data.message || "Token refresh failed");
  }

  localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, data.accessToken);
  return data.accessToken;
};

/**
 * Logout
 */
export const logout = async () => {
  try {
    const refreshToken = getRefreshToken();
    const accessToken = getAccessToken();

    await fetch(`${MAINAPIURL}/auth/logout.php`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ refreshToken }),
    });
  } catch (error) {
    console.error("Logout error:", error);
  } finally {
    clearAuthData();
  }
};

export default {
  login,
  logout,
  refreshAccessToken,
  getAccessToken,
  getRefreshToken,
  getUser,
  getAuthItem,
  isAuthenticated,
  clearAuthData,
  setAuthData,
};
